import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, thumb } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Clock, MapPin, Users, Calendar, Minus, Plus, Trash2, ExternalLink } from "lucide-react";
import CouponField from "../components/CouponField";
import { trackViewContent, trackInitiateCheckout } from "../lib/metaPixel";

const TEE_GROUPS = [
  { label: "Youth sizes", sizes: [["Youth XS", "Y-XS"], ["Youth S", "Y-S"], ["Youth M", "Y-M"], ["Youth L", "Y-L"], ["Youth XL", "Y-XL"]] },
  { label: "Adult sizes", sizes: [["S", "S"], ["M", "M"], ["L", "L"], ["XL", "XL"]] },
];
const TEE_PRICE = 20;

export default function ClassDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [company, setCompany] = useState("");
  const isPrivate = !!c?.private;
  useEffect(() => {
    if (!isPrivate) return undefined;
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, [isPrivate]);
  const [coupon, setCoupon] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [guest, setGuest] = useState({ name: "", email: "" });
  const [waitlist, setWaitlist] = useState({ name: "", email: "" });
  const [waitlisted, setWaitlisted] = useState(false);
  const [tees, setTees] = useState({});
  const [teeImg, setTeeImg] = useState("");
  const [children, setChildren] = useState([{ name: "", allergies: "" }]);
  const [parentPhone, setParentPhone] = useState("");
  const [membership, setMembership] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [ticketMode, setTicketMode] = useState("seats"); // "seats" | "table4"
  const [tableGuests, setTableGuests] = useState(["", "", "", ""]);
  const [activeImg, setActiveImg] = useState(0);
  const [bundles, setBundles] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user && user !== false) {
      api.get("/membership/me").then(({ data }) => setMembership(data)).catch(() => setMembership(null));
    } else {
      setMembership(null);
    }
  }, [user]);

  useEffect(() => {
    api.get(`/classes/${id}`).then(({ data }) => {
      setC(data);
      // Redirect legacy ObjectId links to the clean slug URL
      if (data?.slug && data.slug !== id) nav(`/classes/${data.slug}`, { replace: true });
      if (data) trackViewContent({ id: data.id, name: data.title, price: data.price, contentType: "event" });
    }).catch(() => {});
    api.get("/settings").then(({ data }) => setTeeImg(data?.tshirt_image || "")).catch(() => {});
    api.get("/camp-bundles").then(({ data }) => setBundles(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const teeList = Object.entries(tees).filter(([, q]) => q > 0).map(([size, qty]) => ({ size, qty }));
  const teeQty = teeList.reduce((s, t) => s + t.qty, 0);
  const teeTotal = teeQty * TEE_PRICE;
  const setSize = (size, delta) =>
    setTees((prev) => ({ ...prev, [size]: Math.max(0, Math.min(20, (prev[size] || 0) + delta)) }));

  const doCheckout = async () => {
    if (c.ask_company && !company.trim()) { toast.error("Please enter your company name."); return; }
    setBusy(true);
    trackInitiateCheckout({ id: c.id, name: c.title, value: finalTotal, numItems: isKids ? nKids : (isTable4 ? 4 : quantity) });
    try {
      const payload = { item_type: "class", item_id: c.id, origin_url: window.location.origin };
      if (giftCode.trim()) payload.gift_code = giftCode.trim().toUpperCase();
      if (coupon?.code) payload.coupon_code = coupon.code;
      if (c.ask_company) payload.company_name = company.trim();
      if (teeList.length) payload.tshirts = teeList;
      if (isKids) {
        payload.parent_phone = parentPhone.trim();
        payload.children = children.map((ch) => ({ name: ch.name.trim(), allergies: (ch.allergies || "").trim() }));
      } else if (isTable4) {
        payload.table_for_four = true;
        payload.children = tableGuests.filter((n) => n.trim()).map((n) => ({ name: n.trim() }));
      } else {
        payload.quantity = quantity;
      }
      if (!user || user === false) { payload.guest_name = guest.name.trim(); payload.guest_email = guest.email.trim(); }
      const { data } = await api.post("/payments/checkout", payload);
      if (data.free && data.session_id) { nav(`/payment/success?session_id=${data.session_id}`); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const book = () => {
    if (isKids) {
      if (children.some((ch) => !ch.name.trim())) { toast.error("Please enter a name for each child."); return; }
      if (!parentPhone.trim()) { toast.error("Please enter a parent cell phone number."); return; }
    }
    if (!user || user === false) {
      if (!guest.name.trim()) { toast.error("Please enter your name."); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guest.email.trim())) { toast.error("Please enter a valid email address."); return; }
    }
    if (!agreed) { toast.error("Please agree to the cancellation & transfer policy to continue."); return; }
    doCheckout();
  };

  const joinWaitlist = async () => {
    if (!waitlist.name.trim()) { toast.error("Please enter your name."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(waitlist.email.trim())) { toast.error("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      await api.post(`/classes/${c.id}/waitlist`, { name: waitlist.name.trim(), email: waitlist.email.trim() });
      setWaitlisted(true);
      toast.success("You're on the waitlist! We'll email you if a spot opens.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!c) return <div className="pt-32 px-6 text-stone-500">Loading…</div>;
  const isKids = c.category === "Kids Camps";
  const bundle = bundles.find((b) => (b.camp_ids || []).includes(c.id));
  const seatsLeft = c.capacity - c.seats_booked;
  const rsvpClosed = !!c.rsvp_closed;
  const soldOut = seatsLeft <= 0 || rsvpClosed;
  const rsvpLabel = c.rsvp_by ? new Date(`${c.rsvp_by}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "";
  const single = c.price || 0;
  const pairPrice = c.pair_price || single * 2;
  const nKids = children.length;
  const kidsTotal = Math.floor(nKids / 2) * pairPrice + (nKids % 2) * single;
  const canTable4 = !isKids && !!c.offer_table_for_four && seatsLeft >= 4;
  const isTable4 = canTable4 && ticketMode === "table4";
  const tableFourPrice = Number(c.table_four_price) || 140;
  const classTotal = isKids ? kidsTotal : (isTable4 ? tableFourPrice : single * quantity);
  const memberActive = !!membership?.active;
  const memberPct = (membership?.discount_percent ?? 15) / 100;
  const fullSubtotal = classTotal + teeTotal;
  const memberSavings = memberActive ? Math.round(fullSubtotal * memberPct * 100) / 100 : 0;
  const classAfterMember = memberActive ? Math.round(classTotal * (1 - memberPct) * 100) / 100 : classTotal;
  const couponDiscount = coupon
    ? (coupon.discount_type === "percent"
        ? Math.round(classAfterMember * (coupon.discount_value / 100) * 100) / 100
        : Math.min(coupon.discount_value, classAfterMember))
    : 0;
  const finalTotal = Math.max(0, Math.round((fullSubtotal - memberSavings - couponDiscount) * 100) / 100);
  const addChild = () => { if (nKids < seatsLeft) setChildren([...children, { name: "", allergies: "" }]); };
  const removeChild = (i) => setChildren(children.filter((_, j) => j !== i));
  const updateChild = (i, field, val) => setChildren(children.map((ch, j) => (j === i ? { ...ch, [field]: val } : ch)));
  const gallery = [c.image_url, ...(c.images || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div data-testid="class-detail-page" className="pt-32 pb-24 fade-in">
      <div className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16 grid lg:grid-cols-2 gap-16">
        <div>
          <div className="rounded-3xl overflow-hidden aspect-square">
            <img src={gallery[Math.min(activeImg, gallery.length - 1)] || c.image_url} alt={c.title} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div data-testid="class-gallery-thumbs" className="mt-3 grid grid-cols-5 gap-2">
              {gallery.map((img, i) => (
                <button key={i} data-testid={`class-thumb-${i}`} onClick={() => setActiveImg(i)}
                  className={`rounded-xl overflow-hidden aspect-square border-2 transition-all ${i === Math.min(activeImg, gallery.length - 1) ? "border-[#E0218A]" : "border-transparent hover:border-[#121212]/40"}`}>
                  <img src={thumb(img, 200)} alt={`${c.title} photo ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          {c.private ? (
            <p data-testid="private-event-badge" className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#121212] text-[#C6FF33] text-xs font-sans tracking-[0.25em] uppercase font-bold">Private event · By invitation</p>
          ) : (
            <p className="text-xs font-sans tracking-[0.3em] uppercase text-[#121212]">In-person workshop</p>
          )}
          <h1 className="font-serif text-5xl text-stone-900 mt-4 leading-[1.05]">{c.title}</h1>
          <p className="mt-6 text-stone-600 leading-relaxed text-lg">{c.description}</p>

          {c.rsvp_by && !rsvpClosed && (
            <p data-testid="rsvp-by-note" className="mt-6 inline-block px-4 py-2 bg-[#C6FF33] border-2 border-[#121212] text-sm font-bold text-[#121212]">RSVP by {rsvpLabel}</p>
          )}
          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-[#F5EFE6]">
              <Calendar size={18} className="text-[#121212]" />
              <p className="text-xs tracking-wider uppercase text-stone-500 mt-2">Date</p>
              <p className="text-stone-900 font-medium">{c.date}</p>
            </div>
            <div className="p-4 rounded-xl bg-[#F5EFE6]">
              <Clock size={18} className="text-[#121212]" />
              <p className="text-xs tracking-wider uppercase text-stone-500 mt-2">Duration</p>
              <p className="text-stone-900 font-medium">{c.duration}</p>
            </div>
            <div className="p-4 rounded-xl bg-[#F5EFE6]">
              <Users size={18} className="text-[#121212]" />
              <p className="text-xs tracking-wider uppercase text-stone-500 mt-2">Seats left</p>
              <p className="text-stone-900 font-medium">{c.capacity - c.seats_booked}</p>
            </div>
            <div className="p-4 rounded-xl bg-[#F5EFE6]">
              <MapPin size={18} className="text-[#121212]" />
              <p className="text-xs tracking-wider uppercase text-stone-500 mt-2">Location</p>
              <p className="text-stone-900 font-medium">{c.location}</p>
            </div>
          </div>

          <div data-testid="cancellation-policy" className="mt-10 border-2 border-[#121212] bg-[#F5EFE6] p-5">
            <p className="font-serif font-black text-xl text-[#121212]">Class Cancellation Policy</p>
            <p className="mt-2 text-sm text-stone-700 leading-relaxed">
              All class registrations are nonrefundable. Registration may be transferred one time to another person or moved to another available class date when requested at least 24 hours before the scheduled class. All transfers are subject to availability. Transfers requested within 24 hours of class time and no-shows cannot be accommodated.
            </p>
          </div>

          <div className="mt-10 pt-8 border-t border-stone-200">
            {c.external_booking_url ? (
            <div data-testid="external-booking-card" className="border-2 border-[#121212] p-6 bg-[#F5EFE6]">
              <p className="font-serif font-black text-2xl text-[#121212]">Book this class</p>
              <p className="text-sm text-stone-600 mt-1">Registration for this class is handled on our booking partner's site. Tap below to reserve your spot.</p>
              <a data-testid="external-book-btn" href={c.external_booking_url} target="_blank" rel="noopener noreferrer"
                 className="mt-5 inline-flex items-center justify-center gap-2 w-full px-10 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all">
                Book this class <ExternalLink size={15} />
              </a>
            </div>
            ) : (
            <>
            {bundle && !soldOut && (
              <Link to={`/camps/${bundle.slug}`} data-testid="camp-week-banner" className="mb-6 flex items-center justify-between gap-4 border-2 border-[#121212] bg-[#E0218A] text-white p-5 hover:-translate-y-0.5 transition-transform">
                <span>
                  <span className="block font-serif font-black text-xl">Part of {bundle.title}</span>
                  <span className="block text-sm text-white/85 mt-1">Save when you book 2 or all {bundle.camp_ids.length} camps{bundle.all_camp_price ? ` — all ${bundle.camp_ids.length} for $${bundle.all_camp_price}/child` : ""}.</span>
                </span>
                <span className="shrink-0 px-4 py-2 bg-[#C6FF33] text-[#121212] text-[11px] tracking-[0.2em] uppercase font-bold">Bundle &amp; save</span>
              </Link>
            )}
            {/* Kids Camp: per-child registration */}
            {isKids && !soldOut && (
              <div data-testid="kids-camp-form" className="mb-6 border-2 border-[#121212] p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-serif font-black text-xl text-[#121212]">Who's attending?</p>
                  <p className="text-sm font-bold text-[#121212] whitespace-nowrap">${single} / child · 2 for ${pairPrice}</p>
                </div>
                <p className="text-xs text-stone-500 mt-1">Register one or more children. Save with the 2-child sibling rate — applied automatically in pairs.</p>

                <div className="mt-4 space-y-3">
                  {children.map((ch, i) => (
                    <div key={i} data-testid={`child-row-${i}`} className="border-2 border-stone-200 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]">Child {i + 1}</p>
                        {children.length > 1 && (
                          <button data-testid={`remove-child-${i}`} onClick={() => removeChild(i)} className="text-stone-400 hover:text-red-500"><Trash2 size={16} /></button>
                        )}
                      </div>
                      <input
                        data-testid={`child-name-${i}`}
                        value={ch.name}
                        onChange={(e) => updateChild(i, "name", e.target.value)}
                        placeholder="Child's full name"
                        className="w-full mt-2 px-3 py-2 border-2 border-[#121212] bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none"
                      />
                      <input
                        data-testid={`child-allergies-${i}`}
                        value={ch.allergies}
                        onChange={(e) => updateChild(i, "allergies", e.target.value)}
                        placeholder="Allergies or dietary needs (optional)"
                        className="w-full mt-2 px-3 py-2 border-2 border-stone-200 bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none text-sm"
                      />
                    </div>
                  ))}
                </div>

                <button
                  data-testid="add-child-btn"
                  onClick={addChild}
                  disabled={nKids >= seatsLeft}
                  className="mt-3 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#121212] disabled:opacity-40"
                >
                  <Plus size={14} /> Add another child
                </button>
                {nKids >= seatsLeft && <span className="ml-3 text-xs text-stone-500">Only {seatsLeft} spot{seatsLeft === 1 ? "" : "s"} left in this camp.</span>}

                <div className="mt-4">
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212] block mb-2">Parent cell phone</label>
                  <input
                    data-testid="parent-phone"
                    value={parentPhone}
                    onChange={(e) => setParentPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border-2 border-[#121212] bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* Adult class: ticket type + seats */}
            {!isKids && !soldOut && (
              <div data-testid="seat-selector" className="mb-6 border-2 border-[#121212] p-5">
                {canTable4 && (
                  <div className="mb-4 grid grid-cols-2 gap-2" data-testid="ticket-mode-toggle">
                    <button
                      data-testid="ticket-mode-seats"
                      onClick={() => setTicketMode("seats")}
                      className={`px-3 py-3 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold transition-colors ${ticketMode === "seats" ? "bg-[#C6FF33]" : "bg-white hover:bg-[#F2E9D8]"}`}
                    >
                      Individual seats
                    </button>
                    <button
                      data-testid="ticket-mode-table4"
                      onClick={() => setTicketMode("table4")}
                      className={`px-3 py-3 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold transition-colors ${ticketMode === "table4" ? "bg-[#C6FF33]" : "bg-white hover:bg-[#F2E9D8]"}`}
                    >
                      Table for Four · ${tableFourPrice}
                    </button>
                  </div>
                )}

                {isTable4 ? (
                  <div data-testid="table-four-panel">
                    <p className="font-serif font-black text-xl text-[#121212]">Table for Four</p>
                    <p className="text-xs text-stone-500 mt-1">Reserves 4 seats for ${tableFourPrice}. Enter your guests' names below — you can leave some blank and send the remaining names later.</p>
                    <div className="mt-4 space-y-2">
                      {tableGuests.map((g, i) => (
                        <input
                          key={i}
                          data-testid={`table-guest-${i}`}
                          value={g}
                          onChange={(e) => setTableGuests(tableGuests.map((v, j) => (j === i ? e.target.value : v)))}
                          placeholder={`Guest ${i + 1} name${i === 0 ? "" : " (optional — add later)"}`}
                          className="w-full px-3 py-2 border-2 border-[#121212] bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none"
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-serif font-black text-xl text-[#121212]">How many spots?</p>
                      <p className="text-xs text-stone-500 mt-1">${single} per person · {seatsLeft} {seatsLeft === 1 ? "seat" : "seats"} left</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button data-testid="qty-minus" onClick={() => setQuantity((q) => Math.max(1, q - 1))} disabled={quantity <= 1} className="w-9 h-9 flex items-center justify-center border-2 border-[#121212] disabled:opacity-30 hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors"><Minus size={16} /></button>
                      <span data-testid="qty-value" className="w-8 text-center font-serif font-black text-2xl text-[#121212]">{quantity}</span>
                      <button data-testid="qty-plus" onClick={() => setQuantity((q) => Math.min(seatsLeft, q + 1))} disabled={quantity >= seatsLeft} className="w-9 h-9 flex items-center justify-center border-2 border-[#121212] disabled:opacity-30 hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors"><Plus size={16} /></button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* T-shirt add-on (not offered on private event links) */}
            {!soldOut && !c.private && (
            <div data-testid="tshirt-addon" className="mb-6 border-2 border-[#121212] p-5">
              <div className="flex gap-4">
                {teeImg && (
                  <img src={thumb(teeImg, 240)} alt="Cake Society T-Shirt" data-testid="tshirt-image" className="w-24 h-24 object-cover border-2 border-[#121212] shrink-0 bg-[#A89D8A]/20" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-serif font-black text-xl text-[#121212]">Add a Cake Society Camp Tee</p>
                    <p className="text-sm font-bold text-[#121212] whitespace-nowrap">${TEE_PRICE} each</p>
                  </div>
                  <p className="text-xs text-stone-500 mt-1">Pick it up at the studio on class day. Members save 15% at checkout.</p>
                </div>
              </div>
              <div className="mt-4 space-y-4">
                {TEE_GROUPS.map((group) => (
                  <div key={group.label} data-testid={`tee-group-${group.label.split(" ")[0].toLowerCase()}`}>
                    <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#121212]/60 mb-2">{group.label}</p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {group.sizes.map(([size, disp]) => {
                        const q = tees[size] || 0;
                        const tid = size.replace(/\s+/g, "-");
                        return (
                          <div key={size} data-testid={`tee-size-${tid}`} className={`border-2 ${q > 0 ? "border-[#121212] bg-[#C6FF33]" : "border-stone-200"} p-2 text-center transition-colors`}>
                            <p className="text-xs tracking-[0.1em] uppercase font-bold text-[#121212]">{disp}</p>
                            <div className="mt-2 flex items-center justify-center gap-2">
                              <button data-testid={`tee-minus-${tid}`} onClick={() => setSize(size, -1)} disabled={q === 0} className="w-6 h-6 flex items-center justify-center border border-[#121212] disabled:opacity-30 hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors"><Minus size={12} /></button>
                              <span data-testid={`tee-qty-${tid}`} className="w-5 text-sm font-bold text-[#121212]">{q}</span>
                              <button data-testid={`tee-plus-${tid}`} onClick={() => setSize(size, 1)} className="w-6 h-6 flex items-center justify-center border border-[#121212] hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors"><Plus size={12} /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {teeQty > 0 && (
                <p data-testid="tee-summary" className="mt-3 text-sm text-[#121212]"><strong>{teeQty}</strong> tee{teeQty > 1 ? "s" : ""} added · <strong>${teeTotal}</strong></p>
              )}
            </div>
            )}

            {soldOut ? (
              rsvpClosed ? (
                <div data-testid="rsvp-closed-card" className="border-2 border-[#121212] p-5 bg-[#F5EFE6] text-center">
                  <p className="font-serif font-black text-2xl text-[#121212]">RSVPs for this event are closed</p>
                  <p className="text-sm text-stone-600 mt-1">The RSVP deadline was {rsvpLabel}. Need to join late?</p>
                  <a data-testid="rsvp-contact-link" href={`mailto:cakesocietystudio@gmail.com?subject=${encodeURIComponent(`Late RSVP: ${c.title}`)}`}
                    className="inline-block mt-3 px-6 py-3 bg-[#121212] text-[#C6FF33] text-xs tracking-[0.2em] uppercase font-bold">Contact us</a>
                </div>
              ) : c.booked_out ? (
                <div data-testid="fully-booked-card" className="border-2 border-[#121212] p-5 bg-[#F5EFE6] text-center">
                  <p className="font-serif font-black text-2xl text-[#121212]">Fully booked</p>
                  <p className="text-sm text-stone-600 mt-1">This camp is full. Follow us or check the schedule for future dates!</p>
                </div>
              ) : (
              <div data-testid="waitlist-card" className="border-2 border-[#121212] p-5 bg-[#F5EFE6]">
                <p className="font-serif font-black text-2xl text-[#121212]">This class is sold out</p>
                {waitlisted ? (
                  <p data-testid="waitlist-done" className="mt-2 text-[#121212]">You're on the waitlist — we'll email you the moment a spot opens. 🎉</p>
                ) : (
                  <>
                    <p className="text-sm text-stone-600 mt-1">Join the waitlist and we'll email you if a spot opens up.</p>
                    <div className="mt-4 space-y-2">
                      <input data-testid="waitlist-name" value={waitlist.name} onChange={(e) => setWaitlist({ ...waitlist, name: e.target.value })} placeholder="Your name" className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" />
                      <input data-testid="waitlist-email" type="email" value={waitlist.email} onChange={(e) => setWaitlist({ ...waitlist, email: e.target.value })} placeholder="Your email" className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" />
                      <button data-testid="join-waitlist-btn" onClick={joinWaitlist} disabled={busy} className="w-full px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all">
                        {busy ? "Joining…" : "Join the waitlist"}
                      </button>
                    </div>
                  </>
                )}
              </div>
              )
            ) : (
              <>
                <input
                  data-testid="class-gift-code"
                  value={giftCode}
                  onChange={(e) => setGiftCode(e.target.value)}
                  placeholder="Have a gift code? GIFT-XXXX-XXXX"
                  className="w-full px-4 py-3 mb-4 border-2 border-[#121212] bg-white focus:outline-none uppercase font-mono text-sm"
                />
                <CouponField amount={classTotal} applied={coupon} onChange={setCoupon} context="classes" testid="class-coupon" />

                {c.ask_company && (
                  <input data-testid="class-company-name" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name (required)"
                    className="w-full px-4 py-3 mb-4 border-2 border-[#121212] bg-white focus:outline-none" />
                )}
                {(!user || user === false) && (
                  <div data-testid="guest-details" className="mb-4">
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input data-testid="guest-name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="Your name" className="px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" />
                      <input data-testid="guest-email" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="Your email" className="px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none" />
                    </div>
                    <p data-testid="member-signin-nudge" className="text-xs text-[#121212]/70 mt-2">
                      Cake Society member?{" "}
                      <button type="button" onClick={() => nav(`/login?next=${encodeURIComponent(window.location.pathname)}`)} className="font-bold underline hover:text-[#E0218A]">Sign in</button>
                      {" "}to apply your 15% member discount.
                    </p>
                  </div>
                )}

                <label data-testid="policy-agree" className="flex items-start gap-3 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="policy-agree-checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-[#121212] shrink-0"
                  />
                  <span className="text-sm text-[#121212] leading-snug">
                    I understand that class registrations are nonrefundable and that transfers require at least 24 hours' notice.
                  </span>
                </label>

                <div className="flex items-center justify-between">
                  <div>
                    <span data-testid="class-total" className="font-serif text-4xl text-stone-900">${finalTotal}</span>
                    {memberSavings > 0 && <span data-testid="member-savings" className="block text-xs text-[#E0218A] font-bold mt-1">Member 15% off: −${memberSavings.toFixed(2)}</span>}
                    {couponDiscount > 0 && <span className="block text-xs text-[#121212] mt-1">Coupon {coupon.code}: −${couponDiscount.toFixed(2)}</span>}
                    {isKids && <span className="block text-xs text-stone-500 mt-1">{nKids} {nKids === 1 ? "child" : "children"} · ${classTotal}{teeQty > 0 ? ` + $${teeTotal} tees` : ""}</span>}
                    {!isKids && (quantity > 1 || teeQty > 0) && <span className="block text-xs text-stone-500 mt-1">{quantity} × ${single}{teeQty > 0 ? ` + $${teeTotal} tees` : ""}</span>}
                  </div>
                  <button
                    data-testid="book-class-btn"
                    onClick={book}
                    disabled={busy || !agreed}
                    className="px-10 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-xs tracking-[0.25em] uppercase font-bold transition-all"
                  >
                    {busy ? "Loading…" : "Reserve my spot"}
                  </button>
                </div>
              </>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
